import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useRef } from 'react';
import { useTransitionController } from 'util/react-transition-component';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import styles from './NavigationList.module.scss';
import {
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
} from './NavigationList.transitions';
import type { NavigationListProps } from './NavigationList.data';
import PrimaryLink from '../../../atoms/primary-link/PrimaryLink';
import { useGlobalStore } from '../../../../stores/global';
import LocaleSelector from '../../locale-selector/LocaleSelector';
import MenuItem from '../../../atoms/menu-item/MenuItem';

export default function NavigationList({
  isVisible,
  className,
  menu,
  ref,
}: NavigationListProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLUListElement | null>;
  const listItemsRef = useRef<Array<HTMLElement>>([]);

  const globalStore = useGlobalStore();

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
        listItemsRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, isVisible);

  return (
    <ul ref={elementRef} className={classNames(styles.navigationList, className)}>
      {globalStore?.globalSettings?.nav_items?.map((aHrefLink, index) => (
        <li
          key={`site_nav_link_${index}`}
          className={classNames(styles.listItem)}
          ref={(ref) => {
            listItemsRef.current[index] = ref as HTMLElement;
          }}
        >
          {menu ? (
            elementRef?.current && (
              <MenuItem link={{ href: aHrefLink.link.cached_url, uuid: aHrefLink.link.id }}>
                {aHrefLink.title}
              </MenuItem>
            )
          ) : (
            <PrimaryLink
              textVariant="button-label-large"
              className={classNames(styles.primaryNavAnchor)}
              link={{ href: aHrefLink.link.cached_url, uuid: aHrefLink.link.id }}
              label={aHrefLink.title}
              target={aHrefLink.link.target}
            />
          )}
        </li>
      ))}
      <li
        className={classNames(styles.localeSelectorContainer, styles.listItem)}
        ref={(ref) => {
          listItemsRef.current[globalStore?.globalSettings?.nav_items?.length || 0] =
            ref as HTMLElement;
        }}
      >
        {elementRef?.current && <LocaleSelector menu={menu} />}
      </li>
    </ul>
  );
}
